import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import {
  Search,
  UserPlus,
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn =
  "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const nameIdx = header.findIndex((h) => h.includes("name"));
  const mobileIdx = header.findIndex(
    (h) => h.includes("mobile") || h.includes("phone") || h.includes("number")
  );
  const tagIdx = header.findIndex((h) => h.includes("tag"));
  // If no header keywords found, assume col0=name, col1=mobile with header row skipped
  const ni = nameIdx === -1 ? 0 : nameIdx;
  const mi = mobileIdx === -1 ? 1 : mobileIdx;
  const start = nameIdx === -1 && mobileIdx === -1 ? 0 : 1;
  const rows = [];
  for (let i = start; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    const mobile = (cols[mi] || "").replace(/["']/g, "");
    const name = (cols[ni] || "").replace(/["']/g, "");
    const tag = tagIdx === -1 ? "" : (cols[tagIdx] || "").replace(/["']/g, "");
    if (mobile) rows.push({ name: name || mobile, mobile, tag });
  }
  return rows;
}

function TagPill({ name, color }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ background: `${color || "#DB2777"}22`, color: color || "#DB2777" }}
    >
      {name}
    </span>
  );
}

export default function Contacts() {
  const { activeBranchId } = useBranch();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [tags, setTags] = useState([]);
  const tagMap = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", tags: [] });
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ tags: [] });
  const [savingEdit, setSavingEdit] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const [batchTagId, setBatchTagId] = useState("");

  const loadTags = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get("/tags", { params: { branch_id: activeBranchId } });
      setTags(data);
    } catch (e) { /* ignore */ }
  }, [activeBranchId]);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { branch_id: activeBranchId, search, tag_id: tagFilter || undefined },
      });
      setContacts(data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search, tagFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const toggleFormTag = (id) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(id) ? f.tags.filter((x) => x !== id) : [...f.tags, id] }));

  const toggleEditTag = (id) =>
    setEditForm((f) => ({ ...f, tags: f.tags.includes(id) ? f.tags.filter((x) => x !== id) : [...f.tags, id] }));

  const addContact = async () => {
    setSaving(true);
    try {
      await api.post("/contacts", {
        branch_id: activeBranchId,
        name: form.name,
        mobile: form.mobile,
        tags: form.tags,
      });
      toast.success("Contact added");
      setAddOpen(false);
      setForm({ name: "", mobile: "", tags: [] });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c) => {
    setEditTarget(c);
    setEditForm({ tags: c.tags || [] });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/contacts/${editTarget.id}`, { tags: editForm.tags });
      toast.success("Tags updated");
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    setImportRows(parseCsv(text));
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/contacts/import", {
        branch_id: activeBranchId,
        rows: importRows,
        batch_tag_id: batchTagId || null,
      });
      toast.success(`Imported ${data.imported}, skipped ${data.skipped} duplicate(s)`);
      setImportOpen(false);
      setImportRows([]);
      setFileName("");
      setBatchTagId("");
      await load();
      await loadTags();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "");

  return (
    <motion.div data-testid="contacts-page" variants={containerVariants} initial="hidden" animate="show">
      <motion.div variants={sectionVariants} className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">
            Contacts
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Members for the selected branch.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="import-csv-button"
            className={secondaryBtn}
            onClick={() => {
              setImportRows([]);
              setFileName("");
              setImportOpen(true);
            }}
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
          <button
            data-testid="add-contact-button"
            className={primaryBtn}
            onClick={() => setAddOpen(true)}
          >
            <UserPlus className="h-4 w-4" />
            Add Contact
          </button>
        </div>
      </motion.div>

      <motion.div variants={sectionVariants} className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="contacts-search-input"
            className={`${inputCls} pl-9`}
            placeholder="Search name or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="contacts-tag-filter"
          className={`${inputCls} max-w-[180px]`}
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
        >
          <option value="">All tags</option>
          {tags.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </motion.div>

      <motion.div variants={sectionVariants} className="border border-white/10 hover:border-white/20 transition-colors duration-200 rounded-lg overflow-hidden bg-[#141414]">
        <Table>
          <TableHeader>
            <TableRow className="bg-white/5">
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Name</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Mobile</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Tags</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Status</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Opted Out</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Created</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-400 text-sm">
                  No contacts yet. Add one or import a CSV.
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((c) => (
                <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                  <TableCell className="font-semibold text-white">{c.name}</TableCell>
                  <TableCell className="text-slate-300">{c.mobile}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(c.tags || []).map((tid) => tagMap[tid] && (
                        <TagPill key={tid} name={tagMap[tid].name} color={tagMap[tid].color} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-semibold text-green-400 bg-green-500/10 rounded px-2 py-0.5 capitalize">
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {c.opted_out ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                        <XCircle className="h-3.5 w-3.5" /> Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500">
                        <CheckCircle2 className="h-3.5 w-3.5" /> No
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`edit-contact-tags-${c.id}`}
                      title="Edit tags"
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-white/10 hover:text-white transition-all duration-150"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </motion.div>

      {/* Add contact dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Name</label>
              <input
                data-testid="contact-name-input"
                className={`mt-1.5 ${inputCls}`}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Mobile</label>
              <input
                data-testid="contact-mobile-input"
                className={`mt-1.5 ${inputCls}`}
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="e.g. 919876543210"
              />
            </div>
            {tags.length > 0 && (
              <div>
                <label className={labelCls}>Tags</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`form-tag-${t.id}`}
                      onClick={() => toggleFormTag(t.id)}
                      className={`rounded-full border px-2.5 py-1 transition-all duration-150 ${
                        form.tags.includes(t.id) ? "border-transparent" : "border-white/15 opacity-60 hover:opacity-100"
                      }`}
                      style={form.tags.includes(t.id) ? { background: `${t.color}22`, color: t.color } : {}}
                    >
                      <span className="text-xs font-semibold">{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setAddOpen(false)}>
              Cancel
            </button>
            <button
              data-testid="save-contact-button"
              className={primaryBtn}
              onClick={addContact}
              disabled={saving || !form.name.trim() || !form.mobile.trim()}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit contact tags dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editTarget?.name} — Tags</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {tags.length === 0 ? (
              <p className="text-sm text-slate-400">No tags yet — create one on the Tags page first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`edit-form-tag-${t.id}`}
                    onClick={() => toggleEditTag(t.id)}
                    className={`rounded-full border px-2.5 py-1 transition-all duration-150 ${
                      editForm.tags.includes(t.id) ? "border-transparent" : "border-white/15 opacity-60 hover:opacity-100"
                    }`}
                    style={editForm.tags.includes(t.id) ? { background: `${t.color}22`, color: t.color } : {}}
                  >
                    <span className="text-xs font-semibold">{t.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setEditTarget(null)}>
              Cancel
            </button>
            <button
              data-testid="save-contact-tags-button"
              className={primaryBtn}
              onClick={saveEdit}
              disabled={savingEdit}
            >
              {savingEdit && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-csv-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              CSV needs <span className="font-semibold">name</span> and{" "}
              <span className="font-semibold">mobile</span> columns. An optional{" "}
              <span className="font-semibold">tag</span> column tags each contact
              individually (new tag names are created automatically). Duplicates by
              mobile are skipped, but still get any new tags added.
            </p>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md py-6 cursor-pointer hover:border-orange-400 transition-all duration-150">
              <Upload className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-300">
                {fileName || "Choose a .csv file"}
              </span>
              <input
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onFile}
              />
            </label>

            <div>
              <label className={labelCls}>Assign tag to imported contacts (optional)</label>
              <p className="text-xs text-slate-500 mt-1 mb-1.5">
                Applied to every contact in this import, in addition to any tag mapped from the file.
              </p>
              <select
                data-testid="import-batch-tag-select"
                className={inputCls}
                value={batchTagId}
                onChange={(e) => setBatchTagId(e.target.value)}
              >
                <option value="">No tag</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {importRows.length > 0 && (
              <div data-testid="csv-preview">
                <p className={labelCls}>
                  Preview — first 5 of {importRows.length} rows
                </p>
                <div className="mt-2 border border-white/10 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-white/5">
                        <TableHead className="text-xs font-bold text-slate-500">Name</TableHead>
                        <TableHead className="text-xs font-bold text-slate-500">Mobile</TableHead>
                        <TableHead className="text-xs font-bold text-slate-500">Tag</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importRows.slice(0, 5).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{r.name}</TableCell>
                          <TableCell className="text-sm">{r.mobile}</TableCell>
                          <TableCell className="text-sm text-slate-400">{r.tag || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setImportOpen(false)}>
              Cancel
            </button>
            <button
              data-testid="run-import-button"
              className={primaryBtn}
              onClick={runImport}
              disabled={importing || importRows.length === 0}
            >
              {importing && <Loader2 className="h-4 w-4 animate-spin" />}
              Import {importRows.length > 0 ? `${importRows.length}` : ""}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
